import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Sparkles, Bell, ShoppingBag, User, 
  LayoutDashboard, Calendar, Star, Shield, 
  Building2, Settings, LogOut, Menu, X,
  ChevronDown, MessageSquare
} from "lucide-react";
import { useAuth } from "../App";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "../i18n";
import { T } from "../tokens";

export default function Navbar({ cartCount = 0 }) {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const isImpersonating = !!localStorage.getItem("fyz_admin_session");

  const NAV_LINKS = [
    { key: "home",       label: t("nav.home"),       path: user ? "/dashboard" : "/" },
    { key: "salons",     label: t("nav.salons"),     path: "/salons"    },
    { key: "membership", label: t("nav.membership"), path: "/membership" },
    { key: "shop",       label: t("nav.shop"),       path: "/shop"      },
    { key: "academy",    label: t("nav.academy"),    path: "/academy"   },
    { key: "blog",       label: t("nav.blog"),       path: "/blog"      },
  ];

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h); 
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const h = () => setDropOpen(false);
    document.addEventListener("click", h); 
    return () => document.removeEventListener("click", h);
  }, []);

  const active = (path) => location.pathname === path || (path !== "/" && path !== "/dashboard" && location.pathname.startsWith(path));

  return (
    <>
      <style>{`
        .fyz-nav-link {
          position: relative;
          color: ${T.muted};
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .fyz-nav-link:hover {
          color: ${T.goldLt} !important;
        }
        .fyz-nav-link.active {
          color: ${T.gold} !important;
        }
        .fyz-nav-link::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 50%;
          width: 0;
          height: 2px;
          background: ${T.gold};
          transition: all 0.3s ease;
          transform: translateX(-50%);
          border-radius: 2px;
        }
        .fyz-nav-link.active::after {
          width: 12px;
        }
        .fyz-icon-btn {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(28, 23, 16, 0.4);
          border: 1px solid ${T.goldBdr};
          color: ${T.muted};
          cursor: pointer;
          transition: all 0.25s ease;
        }
        .fyz-icon-btn:hover {
          background: ${T.goldDim};
          border-color: ${T.goldBdrS};
          color: ${T.goldLt};
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
        @media (max-width: 1024px) {
          .fyz-dsk-links { display: none !important; }
          .fyz-mobile-toggle { display: flex !important; }
        }
      `}</style>

      <a 
        href="#main-content" 
        style={{ 
          position: "absolute", left: -9999, top: 0, zIndex: 9999, 
          background: T.gold, color: T.dark, padding: "8px 14px", fontWeight: 600 
        }} 
        onFocus={e => e.currentTarget.style.left = "12px"} 
        onBlur={e => e.currentTarget.style.left = "-9999px"}
      >
        {t("nav.skip_to_content")}
      </a>

      <motion.nav 
        style={{ 
          position: "fixed", top: isImpersonating ? 44 : 0, left: 0, right: 0, zIndex: 200, 
          display: "flex", alignItems: "center", padding: "0 40px", 
          height: scrolled ? 64 : 76, 
          background: scrolled ? "rgba(13, 11, 8, 0.95)" : "rgba(13, 11, 8, 0.8)", 
          backdropFilter: "blur(16px)", 
          borderBottom: `1px solid ${scrolled ? T.goldBdrS : T.goldBdr}`, 
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)", 
          fontFamily: "'DM Sans', sans-serif" 
        }}
      >
        {/* Logo */}
        <button 
          type="button" 
          onClick={() => navigate(user ? "/dashboard" : "/")} 
          aria-label="Fayriza — go to home" 
          style={{ 
            background: "transparent", border: "none", 
            fontFamily: "Cormorant Garamond, serif", fontSize: scrolled ? 26 : 32, 
            color: T.gold, fontStyle: "italic", fontWeight: 600,
            letterSpacing: ".02em", cursor: "pointer", marginRight: 64, 
            flexShrink: 0, padding: 0, transition: "font-size 0.4s" 
          }}
        >
          Fayriza
        </button>

        {/* Desktop Links */}
        <div className="fyz-dsk-links" style={{ display: "flex", gap: 32, flex: 1, alignItems: "center" }}>
          {NAV_LINKS.map(l => (
            <button 
              key={l.key} 
              type="button" 
              className={`fyz-nav-link ${active(l.path) ? 'active' : ''}`} 
              onClick={() => navigate(l.path)} 
              aria-current={active(l.path) ? "page" : undefined} 
              style={{ 
                background: "transparent", border: "none", 
                fontSize: 14, fontWeight: 500, cursor: "pointer", 
                padding: "8px 0", fontFamily: "inherit" 
              }}
            >
              {l.label}
            </button>
          ))}
          {user?.role === "partner" && (
            <button 
              type="button" 
              className={`fyz-nav-link ${active("/partner") ? 'active' : ''}`} 
              onClick={() => navigate("/partner")} 
              style={{ background: "transparent", border: "none", fontSize: 14, fontWeight: 500, cursor: "pointer", padding: "8px 0", fontFamily: "inherit" }}
            >
              {t("nav.partner")}
            </button>
          )}
          {user?.role === "admin" && (
            <button 
              type="button" 
              onClick={() => navigate("/admin")} 
              style={{ 
                background: `linear-gradient(135deg, ${T.goldLt}, ${T.gold})`, 
                border: "none", 
                color: T.dark, 
                fontSize: 12.5, 
                fontWeight: 600, 
                cursor: "pointer", 
                padding: "6px 14px", 
                borderRadius: 20, 
                fontFamily: "inherit",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              onMouseOver={(e) => { 
                e.currentTarget.style.transform = "translateY(-1px)"; 
                e.currentTarget.style.boxShadow = `0 4px 12px rgba(201, 168, 76, 0.3)`; 
              }}
              onMouseOut={(e) => { 
                e.currentTarget.style.transform = "none"; 
                e.currentTarget.style.boxShadow = "none"; 
              }}
            >
              {t("nav.admin")}
            </button>
          )}
        </div>

        {/* Right Side Actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginLeft: "auto" }}>
          <LanguageSwitcher />

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button 
              type="button" 
              className="fyz-icon-btn" 
              onClick={() => navigate("/ai")} 
              title={t("nav.ai_advisor")}
            >
              <Sparkles size={18} strokeWidth={1.5} />
            </button>

            {user && (
              <button 
                type="button" 
                className="fyz-icon-btn" 
                onClick={() => navigate("/notifications")} 
                title={t("nav.notifications")}
              >
                <Bell size={18} strokeWidth={1.5} />
              </button>
            )}

            <button 
              type="button" 
              className="fyz-icon-btn" 
              onClick={() => navigate("/shop")} 
              aria-label={cartCount > 0 ? `${t("nav.cart")}, ${cartCount}` : t("nav.cart")}
              style={{ position: "relative" }}
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              {cartCount > 0 && (
                <span style={{ 
                  position: "absolute", top: -2, right: -2, width: 18, height: 18, 
                  borderRadius: "50%", background: T.gold, fontSize: 10, 
                  color: T.dark, fontWeight: 700, display: "flex", 
                  alignItems: "center", justifyContent: "center",
                  boxShadow: `0 0 0 2px ${T.dark}`
                }}>
                  {cartCount}
                </span>
              )}
            </button>
          </div>

          <div style={{ width: 1, height: 24, background: T.goldBdr, margin: "0 4px" }} />

          {user ? (
            <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
              <button 
                type="button" 
                onClick={() => setDropOpen(v => !v)} 
                style={{ 
                  display: "flex", alignItems: "center", gap: 10, padding: "4px 12px", 
                  borderRadius: 16, background: T.dark3, border: `1px solid ${T.goldBdr}`, 
                  cursor: "pointer", transition: "all 0.2s" 
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.2 }}>{user.name.split(' ')[0]}</span>
                  <span style={{ fontSize: 10, color: T.goldLt, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {user.role === "admin" 
                      ? "Admin" 
                      : user.role === "partner" 
                        ? "Partner" 
                        : (!user.membership_tier || user.membership_tier.toLowerCase() === "none") 
                          ? "Member" 
                          : user.membership_tier}
                  </span>
                </div>
                <div style={{ 
                  width: 36, height: 36, borderRadius: 12, 
                  background: `linear-gradient(135deg, ${T.goldDim}, ${T.dark3})`, 
                  border: `1px solid ${T.goldBdrS}`, display: "flex", 
                  alignItems: "center", justifyContent: "center", overflow: "hidden" 
                }}>
                  {user.avatar ? (
                    <img src={user.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <User size={20} color={T.goldLt} strokeWidth={1.5} />
                  )}
                </div>
              </button>

              <AnimatePresence>
                {dropOpen && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    role="menu" 
                    onClick={e => e.stopPropagation()} 
                    style={{ 
                      position: "absolute", top: 56, right: 0, minWidth: 240, 
                      background: T.dark3, border: `1px solid ${T.goldBdrS}`, 
                      borderRadius: 16, boxShadow: "0 24px 48px rgba(0,0,0,0.6)", 
                      overflow: "hidden", zIndex: 300 
                    }}
                  >
                    <div style={{ padding: "16px", borderBottom: `1px solid ${T.goldBdr}`, background: "rgba(255,255,255,0.02)" }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{user.name}</div>
                      <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{user.email || user.phone}</div>
                    </div>
                    
                    <div style={{ padding: "8px" }}>
                      {[
                        { l: t("nav.dashboard"),    i: LayoutDashboard, p: "/dashboard"  },
                        { l: t("nav.bookings"),     i: Calendar,        p: "/bookings"   },
                        { l: t("nav.orders"),       i: ShoppingBag,     p: "/orders"     },
                        { l: t("nav.chat"),         i: MessageSquare,   p: "/chat"       },
                        { l: t("nav.profile"),      i: User,            p: "/profile"    },
                        { l: t("nav.loyalty"),      i: Star,            p: "/loyalty"    },
                        { l: t("nav.membership"),   i: Shield,          p: "/membership" },
                        ...(user.role === "partner" ? [{ l: t("nav.partner_panel"), i: Building2, p: "/partner" }] : []),
                        ...(user.role === "admin"   ? [{ l: t("nav.admin_panel"),   i: Settings,  p: "/admin"   }] : []),
                      ].map(it => (
                        <button 
                          type="button" 
                          role="menuitem" 
                          key={it.p} 
                          onClick={() => { navigate(it.p); setDropOpen(false); }} 
                          style={{ 
                            width: "100%", textAlign: "left", background: "transparent", 
                            border: "none", display: "flex", alignItems: "center", gap: 12, 
                            padding: "10px 12px", borderRadius: 8, cursor: "pointer", 
                            fontSize: 13, color: T.muted, transition: "all 0.2s", fontFamily: "inherit" 
                          }} 
                          onMouseOver={e => { e.currentTarget.style.background = T.goldDim; e.currentTarget.style.color = T.text; }} 
                          onMouseOut={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.muted; }}
                        >
                          <it.i size={16} strokeWidth={1.5} />
                          {it.l}
                        </button>
                      ))}
                    </div>

                    <div style={{ padding: "8px", borderTop: `1px solid ${T.goldBdr}` }}>
                      <button 
                        type="button" 
                        role="menuitem" 
                        onClick={() => { setShowLogoutConfirm(true); setDropOpen(false); }} 
                        style={{ 
                          width: "100%", textAlign: "left", background: "transparent", 
                          display: "flex", alignItems: "center", gap: 12, 
                          padding: "10px 12px", borderRadius: 8, cursor: "pointer", 
                          fontSize: 13, color: "#F87171", border: "none", fontFamily: "inherit" 
                        }} 
                        onMouseOver={e => e.currentTarget.style.background = "rgba(248,113,113,0.1)"} 
                        onMouseOut={e => e.currentTarget.style.background = "transparent"}
                      >
                        <LogOut size={16} strokeWidth={1.5} />
                        {t("nav.logout")}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <button 
              type="button" 
              onClick={() => navigate("/login")} 
              style={{ 
                padding: "10px 24px", borderRadius: 12, 
                border: `1px solid ${T.gold}`, background: "transparent", 
                color: T.gold, fontSize: 14, fontWeight: 600, 
                cursor: "pointer", fontFamily: "inherit", transition: "all 0.3s" 
              }}
              onMouseOver={e => { e.currentTarget.style.background = T.gold; e.currentTarget.style.color = T.dark; }}
              onMouseOut={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.gold; }}
            >
              {t("nav.login")}
            </button>
          )}

          {/* Mobile Toggle */}
          <button 
            type="button" 
            className="fyz-mobile-toggle" 
            aria-label={menuOpen ? t("common.close") : t("nav.primary_nav")} 
            aria-expanded={menuOpen} 
            style={{ 
              display: "none", cursor: "pointer", color: T.gold, 
              background: "transparent", border: "none", padding: 4 
            }} 
            onClick={() => setMenuOpen(o => !o)}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            role="menu" 
            aria-label={t("nav.mobile_nav")} 
            style={{ 
              position: "fixed", top: scrolled ? 64 : 76, left: 0, right: 0, 
              zIndex: 199, background: "rgba(13, 11, 8, 0.98)", 
              backdropFilter: "blur(20px)", borderBottom: `1px solid ${T.goldBdr}`, 
              padding: "24px", display: "flex", flexDirection: "column", gap: 8,
              overflow: "hidden"
            }}
          >
            {NAV_LINKS.map(l => (
              <button 
                key={l.key} 
                type="button" 
                onClick={() => { navigate(l.path); setMenuOpen(false); }} 
                style={{ 
                  padding: "14px 16px", color: active(l.path) ? T.gold : T.muted, 
                  fontSize: 16, fontWeight: 500, borderRadius: 12,
                  background: active(l.path) ? T.goldDim : "transparent",
                  border: "none", textAlign: "left", fontFamily: "inherit" 
                }}
              >
                {l.label}
              </button>
            ))}
            {!user && (
              <button 
                type="button" 
                onClick={() => { navigate("/login"); setMenuOpen(false); }} 
                style={{ 
                  marginTop: 16, padding: "16px", borderRadius: 12, 
                  background: `linear-gradient(135deg, ${T.goldLt}, ${T.gold})`, 
                  border: "none", color: T.dark, fontSize: 15, 
                  fontWeight: 700, cursor: "pointer", fontFamily: "inherit" 
                }}
              >
                {t("nav.login_register")}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showLogoutConfirm && (
          <div 
            onClick={() => setShowLogoutConfirm(false)}
            style={{ 
              position: "fixed", inset: 0, zIndex: 1000, 
              background: "rgba(0, 0, 0, 0.85)", backdropFilter: "blur(6px)", 
              display: "flex", alignItems: "center", justifyContent: "center" 
            }}
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              onClick={e => e.stopPropagation()}
              style={{ 
                background: T.dark3, border: `1.5px solid ${T.goldBdrS}`, 
                borderRadius: 20, width: "90%", maxWidth: 380, 
                padding: "28px 24px", position: "relative", 
                boxShadow: "0 24px 48px rgba(0,0,0,0.8)",
                fontFamily: "'DM Sans', sans-serif"
              }}
            >
              {/* Alert Icon */}
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
                <div style={{ 
                  width: 56, height: 56, borderRadius: "50%", 
                  background: "rgba(248, 113, 113, 0.1)", border: `1px solid rgba(248, 113, 113, 0.3)`, 
                  display: "flex", alignItems: "center", justifyContent: "center", color: "#F87171" 
                }}>
                  <LogOut size={24} />
                </div>
              </div>

              {/* Text */}
              <h3 style={{ 
                fontFamily: "Cormorant Garamond, serif", fontSize: 24, 
                fontWeight: 600, color: T.text, textAlign: "center", 
                marginBottom: 8 
              }}>
                Confirm Logout
              </h3>
              <p style={{ 
                fontSize: 13.5, color: T.muted, textAlign: "center", 
                lineHeight: 1.6, marginBottom: 24 
              }}>
                Are you sure you want to sign out of your Fayriza account? You will need to sign back in to access your bookings and dashboard.
              </p>

              {/* Actions */}
              <div style={{ display: "flex", gap: 12 }}>
                <button 
                  type="button"
                  onClick={() => setShowLogoutConfirm(false)}
                  style={{ 
                    flex: 1, padding: "11px", borderRadius: 12, 
                    border: `1px solid ${T.goldBdr}`, background: "transparent", 
                    color: T.muted, fontSize: 13.5, fontWeight: 600, 
                    cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" 
                  }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = T.gold; e.currentTarget.style.color = T.text; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = T.goldBdr; e.currentTarget.style.color = T.muted; }}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  onClick={async () => { 
                    setShowLogoutConfirm(false); 
                    await logout(); 
                    navigate("/"); 
                  }}
                  style={{ 
                    flex: 1, padding: "11px", borderRadius: 12, 
                    border: "none", background: "#F87171", 
                    color: T.dark, fontSize: 13.5, fontWeight: 700, 
                    cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" 
                  }}
                  onMouseOver={e => e.currentTarget.style.opacity = 0.9}
                  onMouseOut={e => e.currentTarget.style.opacity = 1}
                >
                  Sign Out
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div id="main-content" tabIndex="-1" style={{ height: scrolled ? 64 : 76, outline: "none" }} />
    </>
  );
}

